import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const { user } = useAuth();
  const reconnectTimeout = useRef(null);

  useEffect(() => {
    if (!user) return;

    const apiBase = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : '/api');
    const socketUrl = apiBase.replace(/\/api\/?$/, '');
    const newSocket = io(socketUrl, {
      auth: { token: localStorage.getItem('token') },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    newSocket.on('connect_error', (err) => {
      console.error('Socket connection error:', err);
    });

    setSocket(newSocket);

    return () => {
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      newSocket.close();
    };
  }, [user]);

  const emit = (event, data) => {
    if (socket?.connected) socket.emit(event, data);
  };

  const on = (event, handler) => {
    if (socket) socket.on(event, handler);
  };

  const off = (event, handler) => {
    if (socket) socket.off(event, handler);
  };

  return (
    <SocketContext.Provider value={{ socket, emit, on, off }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within SocketProvider');
  return ctx;
}